import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { HiXMark, HiClock, HiCalendar } from "react-icons/hi2";
import { Session } from "../../types/post";
import { asDisplayNameOrNull } from "../../utils/displayName";

interface SessionSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionSelected: (sessionId: string) => void;
  availableSessions: (Session & { id: string })[];
  isLoading: boolean;
}

const SessionSelectorModal: React.FC<SessionSelectorModalProps> = ({
  isOpen,
  onClose,
  onSessionSelected,
  availableSessions,
  isLoading,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  // Filter sessions based on search term
  const filteredSessions = availableSessions.filter((session) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const sessionName = (session.name || "").toLowerCase();
    const sessionDate = session.plannedDate.toLowerCase();
    return (
      sessionName.includes(searchLower) || sessionDate.includes(searchLower)
    );
  });

  const handleSessionSelect = (sessionId: string) => {
    onSessionSelected(sessionId);
    onClose();
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString + "T00:00:00");
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4"
      onClick={onClose}
    >
      <div
        className="bg-base-100 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-base-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-base-content font-heading">
            Select Existing Session
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-base-content/50 hover:text-base-content/80 rounded-full hover:bg-base-200 transition-colors"
          >
            <HiXMark className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search sessions by name or date..."
              className="w-full input bg-base-200 text-[16px] sm:text-sm border-base-200 border-2 p-3 rounded-md font-grotesk focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-8">
              <p className="text-sm text-base-content/60 font-grotesk">
                Loading sessions...
              </p>
            </div>
          )}

          {/* Sessions List */}
          {!isLoading && filteredSessions.length === 0 && (
            <div className="text-center py-8 text-base-content/60">
              <p className="text-sm font-grotesk">
                {searchTerm
                  ? "No sessions found matching your search"
                  : "No unlinked completed sessions available"}
              </p>
            </div>
          )}

          {!isLoading && filteredSessions.length > 0 && (
            <div className="space-y-2">
              {filteredSessions.map((session) => (
                <button
                  key={session.id}
                  onClick={() => handleSessionSelect(session.id!)}
                  className="w-full text-left p-4 border border-base-200 rounded-lg hover:border-blue-500 hover:bg-blue-500/10 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-base-content font-heading truncate">
                        {asDisplayNameOrNull(session.name) ||
                          `Session - ${session.plannedDate}`}
                      </h3>
                      <div className="flex items-center gap-4 mt-2 text-xs text-base-content/80 font-grotesk">
                        <div className="flex items-center gap-1">
                          <HiCalendar className="w-4 h-4" />
                          <span>{formatDate(session.plannedDate)}</span>
                        </div>
                        {session.startTime && (
                          <div className="flex items-center gap-1">
                            <HiClock className="w-4 h-4" />
                            <span>
                              {new Date(session.startTime).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </span>
                          </div>
                        )}
                        {session.ActivityMeasurementList && (
                          <span>
                            {(session.ActivityMeasurementList || []).length}{" "}
                            {(session.ActivityMeasurementList || []).length ===
                            1
                              ? "activity"
                              : "activities"}
                          </span>
                        )}
                      </div>
                      {session.description && (
                        <p className="text-xs text-base-content/60 mt-2 line-clamp-2 font-grotesk">
                          {session.description}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionSelectorModal;
